/**
 * Civil-calendar `YYYY-MM-DD` date helpers shared by the dated tool models
 * (expense report, invoice email, trial balance, AR aging).
 *
 * Dates are plain calendar days, never instants: no `Date` object, clock,
 * time zone or DST rule is consulted, so a day difference computed in Tokyo
 * equals the one computed in Los Angeles.
 */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

export function daysInMonth(year: number, month: number): number {
  switch (month) {
    case 2:
      return isLeapYear(year) ? 29 : 28;
    case 4:
    case 6:
    case 9:
    case 11:
      return 30;
    default:
      return 31;
  }
}

/**
 * True for a real `YYYY-MM-DD` calendar date (Feb 29 only in leap years)
 * inside the inclusive `[min, max]` range. `min`/`max` are themselves
 * `YYYY-MM-DD`, so the range check is a plain string comparison.
 */
export function isCalendarDateInRange(
  value: string,
  min: string,
  max: string,
): boolean {
  const match = DATE_RE.exec(value);
  if (!match) return false;
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1) return false;
  if (day > daysInMonth(Number(match[1]), month)) return false;
  return value >= min && value <= max;
}

/**
 * Days since 1970-01-01 of a proleptic-Gregorian civil date, by integer
 * arithmetic alone (Howard Hinnant's `days_from_civil`).
 */
function civilDayNumber(value: string): number {
  const [y, m, d] = value.split("-").map(Number) as [number, number, number];
  const year = m <= 2 ? y - 1 : y;
  const era = Math.floor(year / 400);
  const yearOfEra = year - era * 400;
  const dayOfYear = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const dayOfEra =
    yearOfEra * 365 +
    Math.floor(yearOfEra / 4) -
    Math.floor(yearOfEra / 100) +
    dayOfYear;
  return era * 146_097 + dayOfEra - 719_468;
}

/**
 * Whole calendar days from `from` to `to` (positive when `to` is later).
 * Both must already be valid `YYYY-MM-DD` dates.
 */
export function civilDaysBetween(from: string, to: string): number {
  return civilDayNumber(to) - civilDayNumber(from);
}
